import type { ParameterDefinition, PlotDefinition, PlotKind, PlotSpec } from './types';

const parameter = (
  id: string,
  legacyId: number,
  name: string,
  units: string,
  aggregateColumns: [string, string, string],
  phaseColumns?: string[],
  extras: Partial<ParameterDefinition> = {}
): ParameterDefinition => ({
  id,
  legacyId,
  name,
  units,
  aggregateColumns,
  phaseColumns,
  ranges: false,
  ...extras
});

export const PARAMETERS: ParameterDefinition[] = [
  parameter('current', 0, 'Current', 'A', ['Current_RMS_10_Cycle_Avg_A', 'Current_RMS_10_Cycle_Min_A', 'Current_RMS_10_Cycle_Max_A'], ['L1_Current_RMS_1_2__1_cyc_Avg_A', 'L2_Current_RMS_1_2__1_cyc_Avg_A', 'L3_Current_RMS_1_2__1_cyc_Avg_A', 'N_Current_RMS_1_2__1_cyc_Avg_A'], { harmonicType: 'current', minimum: 0 }),
  parameter('voltage-ln', 1, 'L-N voltage', 'V', ['L_N_RMS_10_Cycle_Avg_V', 'L_N_RMS_10_Cycle_Min_V', 'L_N_RMS_10_Cycle_Max_V'], ['L1_N_RMS_1_2__1_cyc_Avg_V', 'L2_N_RMS_1_2__1_cyc_Avg_V', 'L3_N_RMS_1_2__1_cyc_Avg_V', 'N_E_RMS_1_2__1_cyc_Avg_V'], { harmonicType: 'voltage', plotBands: [{ from: 0, to: 207, label: 'Lower statutory limit', tone: 'danger' }, { from: 253, to: 500, label: 'Upper statutory limit', tone: 'danger' }] }),
  parameter('frequency', 2, 'Frequency, 1-cycle', 'Hz', ['Frequency__1_cyc_Avg_Hz', 'Frequency__1_cyc_Min_Hz', 'Frequency__1_cyc_Max_Hz'], undefined, { ranges: true, plotBands: [{ from: 0, to: 49.5, label: 'Lower statutory limit', tone: 'danger' }, { from: 49.5, to: 49.8, label: 'Lower operational limit', tone: 'warning' }, { from: 50.2, to: 50.5, label: 'Upper operational limit', tone: 'warning' }, { from: 50.5, to: 100, label: 'Upper statutory limit', tone: 'danger' }] }),
  parameter('apparent-power', 3, 'Apparent power', 'kVA', ['Apparent_Power_10_Cycle_Avg_kVA', 'Apparent_Power_10_Cycle_Min_kVA', 'Apparent_Power_10_Cycle_Max_kVA'], ['VA_L1_10_Cycle_Avg_kVA', 'VA_L2_10_Cycle_Avg_kVA', 'VA_L3_10_Cycle_Avg_kVA']),
  parameter('real-power', 4, 'Real power', 'kW', ['Real_Power_10_Cycle_Avg_kW', 'Real_Power_10_Cycle_Min_kW', 'Real_Power_10_Cycle_Max_kW'], ['Real_Power_L1_10_Cycle_Avg_kW', 'Real_Power_L2_10_Cycle_Avg_kW', 'Real_Power_L3_10_Cycle_Avg_kW']),
  parameter('reactive-power', 5, 'Reactive power', 'kvar', ['Reactive_Power_10_Cycle_Avg_kVAR', 'Reactive_Power_10_Cycle_Min_kVAR', 'Reactive_Power_10_Cycle_Max_kVAR'], ['VAR_L1_10_Cycle_Avg_kVAR', 'VAR_L2_10_Cycle_Avg_kVAR', 'VAR_L3_10_Cycle_Avg_kVAR']),
  parameter('power-factor', 6, 'Power factor', '', ['tPF_10_Cycle_Avg', 'tPF_10_Cycle_Min', 'tPF_10_Cycle_Max'], ['tPF_L1_10_Cycle_Avg', 'tPF_L2_10_Cycle_Avg', 'tPF_L3_10_Cycle_Avg']),
  parameter('thd', 7, 'THD', '%', ['THD_V_Avg_perc', 'THD_V_Min_perc', 'THD_V_Max_perc'], ['THD_V_L1_Avg_perc', 'THD_V_L2_Avg_perc', 'THD_V_L3_Avg_perc'], { ranges: true, minimum: 0, plotBands: [{ from: 5, to: 100, label: 'Upper statutory limit', tone: 'danger' }] }),
  parameter('tdd', 8, 'TDD', '%', ['TDD_A_Avg_perc', 'TDD_A_Min_perc', 'TDD_A_Max_perc'], ['TDD_A_L1_Avg_perc', 'TDD_A_L2_Avg_perc', 'TDD_A_L3_Avg_perc'], { ranges: true, minimum: 0 }),
  parameter('negative-sequence-voltage', 9, 'IEC negative-sequence voltage', '%', ['IEC_Negative_Sequence_V_Avg_perc', 'IEC_Negative_Sequence_V_Min_perc', 'IEC_Negative_Sequence_V_Max_perc'], undefined, { ranges: true, minimum: 0 }),
  parameter('zero-sequence-voltage', 10, 'IEC zero-sequence voltage', '%', ['IEC_Zero_Sequence_V_Avg_perc', 'IEC_Zero_Sequence_V_Min_perc', 'IEC_Zero_Sequence_V_Max_perc'], undefined, { ranges: true, minimum: 0 }),
  parameter('negative-sequence-current', 11, 'IEC negative-sequence current', '%', ['IEC_Negative_Sequence_A_Avg_perc', 'IEC_Negative_Sequence_A_Min_perc', 'IEC_Negative_Sequence_A_Max_perc'], undefined, { ranges: true, minimum: 0 }),
  parameter('zero-sequence-current', 12, 'IEC zero-sequence current', '%', ['IEC_Zero_Sequence_A_Avg_perc', 'IEC_Zero_Sequence_A_Min_perc', 'IEC_Zero_Sequence_A_Max_perc'], undefined, { ranges: true, minimum: 0 }),
  parameter('flicker-instantaneous', 13, 'Flicker instantaneous (Pinst)', '', ['Flicker_P_inst_Avg', 'Flicker_P_inst_Min', 'Flicker_P_inst_Max'], ['P_inst_L1_Avg', 'P_inst_L2_Avg', 'P_inst_L3_Avg'], { minimum: 0 }),
  parameter('flicker-short', 14, 'Flicker short-term (Pst)', '', ['Flicker_P_st_Avg', 'Flicker_P_st_Min', 'Flicker_P_st_Max'], ['P_st_L1_Avg', 'P_st_L2_Avg', 'P_st_L3_Avg'], { minimum: 0 }),
  parameter('flicker-long', 15, 'Flicker long-term (Plt)', '', ['Flicker_P_lt_Avg', 'Flicker_P_lt_Min', 'Flicker_P_lt_Max'], ['P_lt_L1_Avg', 'P_lt_L2_Avg', 'P_lt_L3_Avg'], { minimum: 0 })
];

const incompatible = (reason: string) => (_parameter: ParameterDefinition) => reason;
const phaseReason = (p: ParameterDefinition) => p.phaseColumns ? null : `${p.name} has no individual-phase columns.`;
const harmonicReason = (p: ParameterDefinition) => p.harmonicType ? null : `${p.name} has no harmonic snapshots.`;

export const PLOTS: PlotDefinition[] = [
  { kind: 'average-series', legacyId: 0, name: 'Time series (average of phases, with min-max range)', shortName: 'Average time series', timeDomain: 'range', supportsHistogram: true, supportsEvents: true, compatible: () => true, incompatibilityReason: incompatible('') },
  { kind: 'phase-series', legacyId: 1, name: 'Time series (three-phase)', shortName: 'Three-phase time series', timeDomain: 'range', supportsHistogram: true, supportsEvents: true, compatible: (p) => Boolean(p.phaseColumns), incompatibilityReason: phaseReason },
  { kind: 'day-polar', legacyId: 2, name: 'Day polar plot (three-phase)', shortName: 'Day polar plot', timeDomain: 'day', supportsHistogram: true, supportsEvents: false, compatible: (p) => Boolean(p.phaseColumns), incompatibilityReason: phaseReason },
  { kind: 'heatmap', legacyId: 3, name: 'Heat map (average)', shortName: 'Heat map', timeDomain: 'heatmap', supportsHistogram: false, supportsEvents: false, compatible: () => true, incompatibilityReason: incompatible('') },
  { kind: 'harmonic-spectrum', legacyId: 4, name: 'Instantaneous harmonic spectra', shortName: 'Harmonic spectrum', timeDomain: 'snapshot', supportsHistogram: false, supportsEvents: false, compatible: (p) => Boolean(p.harmonicType), incompatibilityReason: harmonicReason },
  { kind: 'waveform', legacyId: 5, name: 'Detailed waveforms (estimated from harmonic profile)', shortName: 'Detailed waveform', timeDomain: 'relative', supportsHistogram: false, supportsEvents: false, compatible: (p) => Boolean(p.harmonicType), incompatibilityReason: harmonicReason },
  { kind: 'harmonic-trend', legacyId: 6, name: 'Harmonics time-series', shortName: 'Harmonic trend', timeDomain: 'range', supportsHistogram: false, supportsEvents: true, compatible: (p) => Boolean(p.harmonicType), incompatibilityReason: harmonicReason }
];

// Preserve the phase identity used by the legacy UI: red, yellow, blue, neutral.
// The chart adapter substitutes a theme-aware neutral colour at render time.
export const PHASE_COLOURS = [
  'rgba(180, 33, 38, 0.8)',
  'rgba(222, 215, 20, 0.8)',
  'rgba(36, 78, 198, 0.8)',
  'rgba(40, 40, 40, 0.8)'
];
export const SERIES_COLOURS = ['#2f7ed8', '#8bbc21', '#910000', '#492970', '#0d233a', '#1aadce', '#f28f43'];
export const PHASE_NAMES = ['L1', 'L2', 'L3', 'Neutral'];

export function parameterById(id: string): ParameterDefinition {
  return PARAMETERS.find((item) => item.id === id) ?? PARAMETERS[0];
}

export function parameterByLegacyId(id: number): ParameterDefinition {
  return PARAMETERS.find((item) => item.legacyId === id) ?? PARAMETERS[0];
}

export function plotByKind(kind: PlotKind): PlotDefinition {
  return PLOTS.find((item) => item.kind === kind) ?? PLOTS[0];
}

export function plotByLegacyId(id: number): PlotDefinition {
  return PLOTS.find((item) => item.legacyId === id) ?? PLOTS[0];
}

export function compatibleParameters(kind: PlotKind): ParameterDefinition[] {
  const definition = plotByKind(kind);
  return PARAMETERS.filter((item) => definition.compatible(item));
}

export function normalizePlotSpec(plot: PlotSpec): PlotSpec {
  const definition = plotByKind(plot.kind);
  let selected = parameterById(plot.parameterId);
  if (!definition.compatible(selected)) selected = compatibleParameters(plot.kind)[0] ?? PARAMETERS[0];
  const showEvents = Boolean(plot.options.showEvents && definition.supportsEvents);
  const selectedEvent = showEvents
    && typeof plot.selectedEvent?.monitorId === 'string'
    && plot.selectedEvent.monitorId.length > 0
    && Number.isSafeInteger(plot.selectedEvent.timestamp)
    && plot.selectedEvent.timestamp >= 0
    ? { ...plot.selectedEvent }
    : undefined;
  const hiddenEventClassifications = Array.from(new Set(
    (plot.hiddenEventClassifications ?? [])
      .filter((key): key is string => typeof key === 'string')
      .map((key) => key.trim())
      .filter((key) => /^(?:cause|status):[a-z0-9][a-z0-9_-]{0,64}$/.test(key))
  )).sort().slice(0, 64);
  return {
    ...plot,
    parameterId: selected.id,
    selectedEvent,
    hiddenEventClassifications: hiddenEventClassifications.length ? hiddenEventClassifications : undefined,
    options: {
      showEvents,
      showHistogram: Boolean(plot.options.showHistogram && definition.supportsHistogram),
      cumulative: Boolean(plot.options.cumulative),
      showNeutral: Boolean(plot.options.showNeutral),
      showFundamental: plot.options.showFundamental !== false,
      perUnit: Boolean(plot.options.perUnit),
      showInterharmonics: Boolean(plot.options.showInterharmonics)
    }
  };
}
